// =====================================================================
// DashboardOperator.jsx — Day 9 Hybrid dashboard for OPERATOR roles
// =====================================================================
// Matches the approved Hybrid mockup (Variant C).
// Self-scoped: only shows own data, plus colleagues off-this-week.
// =====================================================================

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { dashboard as dashboardApi } from '../services/api.js';
import { useAuth } from '../hooks/useAuth.jsx';

function fmtHHMMfromMin(mins) {
  if (!mins) return '0h00';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h${String(m).padStart(2, '0')}`;
}
function fmtHHMMfromHours(h) {
  if (h === null || h === undefined || !Number.isFinite(h) || h === 0) return '0:00';
  const sign = h > 0 ? '+' : '-';
  const totalMin = Math.round(Math.abs(h) * 60);
  const H = Math.floor(totalMin / 60);
  const M = totalMin % 60;
  return `${sign}${H}:${String(M).padStart(2, '0')}`;
}
function fmtDate(ymd) {
  if (!ymd) return '—';
  const [y, m, d] = ymd.split('-');
  return `${d}/${m}/${y}`;
}
function fmtRel(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h}h`;
  const d = Math.floor(h / 24);
  return `il y a ${d}j`;
}
function getInitials(name) {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
const DOW_NAMES = ['DIM', 'LUN', 'MAR', 'MER', 'JEU', 'VEN', 'SAM'];

export default function DashboardOperator() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const d = await dashboardApi.getOperator();
      setData(d);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    const t = setInterval(load, 120 * 1000); // auto-refresh 2 min
    return () => clearInterval(t);
  }, []);

  if (loading && !data) return <div style={{ padding: 32, color: '#64748b' }}>Chargement…</div>;
  if (error) return (
    <div style={{ padding: 24, background: '#fef2f2', color: '#991b1b', borderRadius: 8, margin: 20 }}>
      Erreur : {error}
      <button onClick={load} style={{ marginLeft: 12 }}>Réessayer</button>
    </div>
  );
  if (!data) return null;

  const fmtFR = new Date().toLocaleDateString('fr-BE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Bonjour' : hour < 18 ? 'Bon après-midi' : 'Bonsoir';

  const isWeekend = [0, 6].includes(new Date().getDay());
  const needsEncoding = !data.today.encoded && !isWeekend;

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>

      {/* Greeting */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 22 }}>
        <span style={{
          width: 56, height: 56, borderRadius: '50%',
          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
          color: 'white', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 22, fontWeight: 700, flexShrink: 0,
        }}>{getInitials(data.user.name)}</span>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>{greeting} {data.user.name.split(' ')[0]} 👋</h1>
          <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>{fmtFR} · {data.user.role}{data.user.groupName ? ` · ${data.user.groupName}` : ''}</div>
        </div>
      </div>

      {/* Status banner */}
      {needsEncoding && (
        <div style={{
          background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
          border: '1px solid #f59e0b', borderRadius: 12,
          padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, marginBottom: 22,
        }}>
          <span style={{ fontSize: 28 }}>⏰</span>
          <div style={{ flex: 1 }}>
            <strong style={{ fontSize: 14, display: 'block', color: '#78350f' }}>Tu n'as pas encore encodé tes heures aujourd'hui</strong>
            <span style={{ fontSize: 12, color: '#92400e' }}>Pense à compléter ta journée.</span>
          </div>
          <button onClick={() => navigate('/temps#mes-heures')} style={{ padding: '8px 18px', background: '#f59e0b', color: 'white', border: 0, borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>Encoder →</button>
        </div>
      )}
      {!needsEncoding && data.today.encoded && (
        <div style={{
          background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
          border: '1px solid #16a34a', borderRadius: 12,
          padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, marginBottom: 22,
        }}>
          <span style={{ fontSize: 28 }}>✅</span>
          <div style={{ flex: 1 }}>
            <strong style={{ fontSize: 14, display: 'block', color: '#166534' }}>Ta journée est encodée</strong>
            <span style={{ fontSize: 12, color: '#15803d' }}>{data.today.type} · {fmtHHMMfromMin(data.today.minutes)}</span>
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 22 }}>
        <QuickAction onClick={() => navigate('/temps#mes-heures')} icon="⏱️" bg="#dbeafe" color="#1e40af" title="Encoder TS" sub="timesheet aujourd'hui" />
        <QuickAction onClick={() => navigate('/conges#mes-conges')} icon="🌴" bg="#d1fae5" color="#065f46" title="Demander congé" sub="VA, REC, AA..." />
        <QuickAction onClick={() => navigate('/temps#overtime:soldes-operateurs')} icon="🔥" bg="#fef3c7" color="#78350f" title="Mes heures sup." sub={`solde ${fmtHHMMfromHours(data.overtime.allTimeBalance)}`} />
        <QuickAction onClick={() => navigate('/trajets#mes-courses')} icon="🚚" bg="#fed7aa" color="#7c2d12" title="Mes trajets" sub="dispatch & courses" />
      </div>

      {/* 2-col main */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 18 }}>

        {/* LEFT */}
        <div>
          {/* Soldes */}
          <SectionTitle>💼 MES SOLDES — ANNÉE {new Date().getFullYear()}</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 22 }}>
            <BalTile type="va" label="VA · Congés" bal={data.balances.find((b) => b.type === 'VA')} />
            <BalTile type="rec" label="REC · Récup." bal={data.balances.find((b) => b.type === 'REC')} />
            <BalTile type="aa" label="AA · Abs. Autor." bal={data.balances.find((b) => b.type === 'AA')} />
            <div style={tileStyle('ot')}>
              <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600, marginBottom: 4 }}>🔥 Heures sup.</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', lineHeight: 1 }}>{fmtHHMMfromHours(data.overtime.allTimeBalance)}</div>
              <div style={{ fontSize: 10, color: '#92400e', marginTop: 4 }}>{fmtHHMMfromHours(data.overtime.monthCumul)} ce mois</div>
            </div>
          </div>

          {/* Ma semaine */}
          <SectionTitle>📅 MA SEMAINE</SectionTitle>
          <div style={panelStyle}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8 }}>
              {data.week.days.map((d) => {
                const isToday = d.date === data.today.date;
                const hasEntry = d.entries.length > 0;
                let pipLabel = '— rien';
                let pipBg = '#f1f5f9', pipFg = '#94a3b8';
                if (d.isHoliday) { pipLabel = 'JF'; pipBg = '#fce7f3'; pipFg = '#9f1239'; }
                else if (hasEntry) {
                  const main = d.entries[0];
                  pipLabel = `${main.type} ${fmtHHMMfromMin(d.totalMin)}`;
                  const colors = {
                    TRAV: ['#dcfce7','#166534'], MAL: ['#fee2e2','#991b1b'],
                    AA: ['#fef3c7','#92400e'], REC: ['#f3e8ff','#6b21a8'],
                    VA: ['#dbeafe','#1e40af'], CSSO: ['#e0e7ff','#3730a3'],
                    JF: ['#fce7f3','#9f1239'], HC: ['#f3f4f6','#374151'],
                  };
                  [pipBg, pipFg] = colors[main.type] || ['#f1f5f9','#475569'];
                }
                return (
                  <div key={d.date} style={{
                    background: isToday ? 'linear-gradient(135deg, #ede9fe, #ddd6fe)' : '#f8fafc',
                    border: isToday ? '1px solid #8b5cf6' : '1px solid transparent',
                    borderRadius: 10, padding: '12px 8px', textAlign: 'center', minHeight: 86,
                    opacity: d.isWeekend ? 0.5 : 1,
                  }}>
                    <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>{DOW_NAMES[d.dow]}</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginTop: 2 }}>{d.date.slice(8, 10)}</div>
                    <div style={{ display: 'block', height: 18, background: pipBg, color: pipFg, borderRadius: 4, marginTop: 4, fontSize: 10, padding: '2px 4px', fontWeight: 600 }}>
                      {d.isHoliday ? `JF` : pipLabel}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: 14, fontSize: 12, color: '#64748b', textAlign: 'center' }}>
              🎯 Total cette semaine : <strong style={{ color: '#0f172a' }}>{fmtHHMMfromMin(data.week.totalMin)} / {fmtHHMMfromMin(data.week.targetMin)}</strong>
              {data.week.missingMin > 0 && (
                <span> · Manque <strong style={{ color: '#f59e0b' }}>{fmtHHMMfromMin(data.week.missingMin)}</strong></span>
              )}
            </div>
          </div>

          {/* Demandes en attente */}
          {data.pendingRequests.length > 0 && (
            <>
              <SectionTitle style={{ marginTop: 22 }}>🕓 MES DEMANDES EN ATTENTE ({data.pendingRequests.length})</SectionTitle>
              <div style={panelStyle}>
                {data.pendingRequests.map((r) => (
                  <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#f59e0b' }}></span>
                    <div style={{ flex: 1, fontSize: 13 }}>
                      <div>🌴 Congé {r.type} du {fmtDate(r.startDate)} au {fmtDate(r.endDate)}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{fmtRel(r.submittedAt)}{r.reason ? ` · ${r.reason}` : ''}</div>
                    </div>
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 600, background: '#fef3c7', color: '#78350f' }}>⏳ en attente</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Mes prochains congés */}
          {data.upcomingLeaves.length > 0 && (
            <>
              <SectionTitle style={{ marginTop: 22 }}>📆 MES PROCHAINS CONGÉS</SectionTitle>
              <div style={panelStyle}>
                {data.upcomingLeaves.map((l) => (
                  <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: l.status === 'APPROVED' ? '#16a34a' : '#f59e0b' }}></span>
                    <div style={{ flex: 1, fontSize: 13 }}>
                      <div>{l.type} du {fmtDate(l.startDate)} au {fmtDate(l.endDate)}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{l.status === 'APPROVED' ? `approuvé le ${fmtDate((l.decidedAt || '').slice(0,10))}` : 'en attente de validation'}</div>
                    </div>
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 600,
                      background: l.status === 'APPROVED' ? '#dcfce7' : '#fef3c7',
                      color: l.status === 'APPROVED' ? '#166534' : '#78350f' }}>
                      {l.status === 'APPROVED' ? '✓ approuvé' : 'en attente'}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* RIGHT sticky sidebar */}
        <div style={{ position: 'sticky', top: 16, alignSelf: 'start', display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* VA detail */}
          <div style={panelStyle}>
            <h3 style={sbTitleStyle}>🌴 VA — DÉTAIL</h3>
            <div style={{ textAlign: 'center', margin: '8px 0' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#0f172a' }}>{data.vaDetail.remaining} j</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>restants sur {data.vaDetail.totalAllocated}</div>
            </div>
            <SbRow label="Utilisés" value={`${data.vaDetail.used} j`} />
            <SbRow label="En attente" value={`${data.vaDetail.pending} j`} color={data.vaDetail.pending > 0 ? '#92400e' : '#0f172a'} />
            <SbRow label="Effectif si validé" value={`${data.vaDetail.remaining - data.vaDetail.pending} j`} bold />
          </div>

          {/* Colleagues off */}
          <div style={panelStyle}>
            <h3 style={sbTitleStyle}>👥 COLLÈGUES OFF — 14 JOURS</h3>
            {data.colleaguesOff.length === 0 ? (
              <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', padding: 8 }}>Personne</div>
            ) : data.colleaguesOff.map((c) => (
              <div key={c.userId + c.startDate} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{
                  width: 26, height: 26, borderRadius: '50%',
                  background: '#6366f1', color: 'white',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 10, fontWeight: 700, flexShrink: 0,
                }}>{getInitials(c.userName)}</span>
                <div style={{ flex: 1, fontSize: 12 }}>
                  <div style={{ fontWeight: 500 }}>{c.userName.split(' ')[0]}</div>
                  <div style={{ fontSize: 10, color: '#94a3b8' }}>{fmtDate(c.startDate)}{c.startDate !== c.endDate ? ` → ${fmtDate(c.endDate)}` : ''}</div>
                </div>
                <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 600, background: '#dbeafe', color: '#1e40af' }}>{c.type}</span>
              </div>
            ))}
          </div>

          {/* Astuce */}
          <div style={{ ...panelStyle, background: 'linear-gradient(135deg, #fef3c7, #fde68a)', border: '1px solid #f59e0b' }}>
            <div style={{ fontSize: 12, color: '#78350f', fontWeight: 600, marginBottom: 4 }}>💡 Astuce du jour</div>
            <div style={{ fontSize: 11, color: '#92400e', lineHeight: 1.5 }}>
              Pense à encoder ton timesheet chaque soir avant de partir. Cela évite les oublis et accélère la validation.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ----- Helpers -----

function QuickAction({ onClick, icon, bg, color, title, sub }) {
  return (
    <div onClick={onClick} style={{
      background: 'white', border: '1px solid #e2e8f0', borderRadius: 12,
      padding: '18px 14px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.15s',
    }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 14px rgba(0,0,0,0.08)'; e.currentTarget.style.borderColor = '#6366f1'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
    >
      <div style={{ width: 44, height: 44, borderRadius: 12, margin: '0 auto 8px', background: bg, color, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>{icon}</div>
      <div style={{ fontWeight: 600, fontSize: 13, color: '#0f172a' }}>{title}</div>
      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>{sub}</div>
    </div>
  );
}

function tileStyle(variant) {
  const styles = {
    va:  { background: '#faeeda', borderColor: '#e8d4b0' },
    rec: { background: '#f3e8ff', borderColor: '#d8b4fe' },
    aa:  { background: '#dbeafe', borderColor: '#93c5fd' },
    ot:  { background: '#fef3c7', borderColor: '#fcd34d' },
  }[variant] || { background: '#f8fafc', borderColor: '#e2e8f0' };
  return { ...styles, border: `1px solid ${styles.borderColor}`, borderRadius: 10, padding: 14, textAlign: 'center' };
}

function BalTile({ type, label, bal }) {
  const b = bal || { totalAllocated: 0, used: 0, remaining: 0, pending: 0 };
  return (
    <div style={tileStyle(type)}>
      <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', lineHeight: 1 }}>
        {b.remaining}<span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}> / {b.totalAllocated} j</span>
      </div>
      {b.pending > 0 && <div style={{ fontSize: 10, color: '#92400e', marginTop: 4 }}>{b.pending} en attente</div>}
    </div>
  );
}

function SectionTitle({ children, style }) {
  return <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, margin: '0 0 12px', ...style }}>{children}</div>;
}

function SbRow({ label, value, color, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderTop: '1px solid #f1f5f9', fontSize: 11 }}>
      <span style={{ color: '#64748b' }}>{label}</span>
      <strong style={{ color: color || '#0f172a', fontWeight: bold ? 800 : 700 }}>{value}</strong>
    </div>
  );
}

const panelStyle = { background: 'white', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 };
const sbTitleStyle = { margin: '0 0 8px', fontSize: 11, fontWeight: 700, color: '#0f172a', textTransform: 'uppercase', letterSpacing: 0.5 };
